// boardOrder.js

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const normalizeKeyList = (keys) => (
    Array.isArray(keys)
        ? keys.filter((key) => isNonEmptyString(key)).map((key) => key.trim())
        : []
);

export const normalizeBoardOrder = (boardOrder, boardFieldKey = '') => {
    const fieldKey = isNonEmptyString(boardFieldKey)
        ? boardFieldKey.trim()
        : '';

    if (!fieldKey) {
        return {
            fieldKey: '',
            columns: {},
        };
    }

    const rawFieldKey = typeof boardOrder?.fieldKey === 'string'
        ? boardOrder.fieldKey.trim()
        : '';

    if (!rawFieldKey || rawFieldKey !== fieldKey) {
        return {
            fieldKey,
            columns: {},
        };
    }

    const rawColumns = boardOrder?.columns && typeof boardOrder.columns === 'object'
        ? boardOrder.columns
        : {};

    const columns = Object.entries(rawColumns).reduce((accumulator, [columnKey, keys]) => {
        if (!isNonEmptyString(columnKey)) return accumulator;
        const normalizedKeys = normalizeKeyList(keys);
        if (normalizedKeys.length) {
            accumulator[columnKey.trim()] = normalizedKeys;
        }
        return accumulator;
    }, {});

    return {
        fieldKey,
        columns,
    };
};

export const reconcileOrderedItems = (items = [], orderedKeys = []) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const itemByKey = new Map();
    items.forEach((item) => {
        const itemKey = typeof item?.key === 'string' ? item.key.trim() : '';
        if (itemKey) itemByKey.set(itemKey, item);
    });

    const orderedItems = [];
    normalizeKeyList(orderedKeys).forEach((itemKey) => {
        const item = itemByKey.get(itemKey);
        if (!item) return;
        orderedItems.push(item);
        itemByKey.delete(itemKey);
    });

    items.forEach((item) => {
        const itemKey = typeof item?.key === 'string' ? item.key.trim() : '';
        if (!itemKey || !itemByKey.has(itemKey)) return;
        orderedItems.push(item);
        itemByKey.delete(itemKey);
    });

    return orderedItems;
};

export const buildBoardOrderPayload = ({
    boardFieldKey,
    boardData,
    nodeKey,
    sourceColumnKey,
    targetColumnKey,
    targetInsertIndex,
    excludedColumnKeys = [],
} = {}) => {
    const normalized = normalizeBoardOrder({ fieldKey: boardFieldKey, columns: {} }, boardFieldKey);
    if (!normalized.fieldKey || !nodeKey || !boardData) return normalized;

    const excluded = new Set(normalizeKeyList(excludedColumnKeys));
    const columns = {};

    boardData.columns.forEach((column) => {
        const columnKey = typeof column?.key === 'string' ? column.key.trim() : '';
        if (!columnKey || excluded.has(columnKey)) return;
        const items = boardData.groups.get(columnKey) || [];
        const keys = items
            .map((item) => (typeof item?.key === 'string' ? item.key.trim() : ''))
            .filter(Boolean);
        if (keys.length) {
            columns[columnKey] = keys;
        }
    });

    Object.keys(columns).forEach((columnKey) => {
        columns[columnKey] = columns[columnKey].filter((currentKey) => currentKey !== nodeKey);
        if (!columns[columnKey].length) delete columns[columnKey];
    });

    if (!excluded.has(targetColumnKey)) {
        const targetKeys = [...(columns[targetColumnKey] || [])];
        const sourceKeys = boardData.groups.get(sourceColumnKey) || [];
        const sourceIndex = sourceKeys.findIndex((item) => item?.key === nodeKey);
        let safeInsertIndex = Number.isFinite(targetInsertIndex)
            ? Math.max(0, Math.min(targetInsertIndex, targetKeys.length))
            : targetKeys.length;

        if (sourceColumnKey === targetColumnKey && sourceIndex >= 0 && sourceIndex < safeInsertIndex) {
            safeInsertIndex -= 1;
        }

        targetKeys.splice(safeInsertIndex, 0, nodeKey);
        if (targetKeys.length) {
            columns[targetColumnKey] = targetKeys;
        }
    }

    return {
        fieldKey: normalized.fieldKey,
        columns,
    };
};